import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { Modal, Input, Button, RobbieMark } from '../ui';
import type { RFQEndUser } from '../../types/rfq.types';

/**
 * New RFQ naming popup — the "New RFQ" action asks for the project name
 * BEFORE anything exists: confirming creates the RFQ under that name and the
 * caller navigates into the wizard; Cancel/Escape/backdrop create nothing
 * (navigation never creates records). The name lands on the same field the
 * wizard header rename edits, so the header shows it on arrival.
 *
 * It also asks who the technical END USER is — the person who'll answer the
 * technical questions later (payload, specs, validation fixes). Optional: an
 * empty email means the creator owns those steps themselves. Any email is
 * accepted; the person doesn't need an account yet.
 */
export interface NewRFQModalProps {
  open: boolean;
  onClose: () => void;
  /** Creates the RFQ with the confirmed name; the caller navigates on success. */
  onCreate: (name: string, endUser?: RFQEndUser) => Promise<void>;
  /**
   * Starting value for the field — the first-run flow seeds it from the
   * dropped file so the buyer confirms a name rather than inventing one.
   */
  defaultName?: string;
}

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const NewRFQModal: React.FC<NewRFQModalProps> = ({ open, onClose, onCreate, defaultName = '' }) => {
  const [name, setName] = useState(defaultName);
  const [endUserName, setEndUserName] = useState('');
  const [endUserTeam, setEndUserTeam] = useState('');
  const [endUserEmail, setEndUserEmail] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [emailError, setEmailError] = useState<string | undefined>();
  const inputRef = useRef<HTMLInputElement>(null);

  // Reseed each time it opens: a second drop must not show the first file's name.
  useEffect(() => {
    if (open) {
      setName(defaultName);
      setEndUserName('');
      setEndUserTeam('');
      setEndUserEmail('');
      setEmailError(undefined);
    }
  }, [open, defaultName]);

  const close = () => {
    if (creating) return;
    setName(defaultName);
    setError(undefined);
    setEmailError(undefined);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    const email = endUserEmail.trim().toLowerCase();
    if (!trimmed || creating) return;
    if (email && !EMAIL_SHAPE.test(email)) {
      setEmailError('That email doesn’t look right.');
      return;
    }
    setCreating(true);
    setError(undefined);
    setEmailError(undefined);
    try {
      const endUser: RFQEndUser | undefined = email
        ? {
            email,
            name: endUserName.trim() || undefined,
            teamName: endUserTeam.trim() || undefined,
          }
        : undefined;
      await onCreate(trimmed, endUser);
    } catch {
      setError("I couldn't create the RFQ — give it another try.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={close}
      size="sm"
      initialFocusRef={inputRef}
      title={
        <span className="flex items-center gap-s3">
          {/* RFQ = the artifact, project = the human name (owner copy rule). */}
          <RobbieMark size="sm" aria-label="Robbie" />
          New RFQ
        </span>
      }
    >
      <form onSubmit={handleSubmit} noValidate>
        <p className="mb-s5 text-body-sm text-ink-secondary">
          Name your project — you can rename it anytime.
        </p>
        <Input
          ref={inputRef}
          label="Project name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Delivery drone"
          required
          disabled={creating}
          error={error}
        />
        {/* End user = whoever answers the technical questions later. Optional:
            blank means the creator owns those steps themselves. */}
        <p className="mt-s5 mb-s3 text-body-sm text-ink-secondary">
          Who&rsquo;ll fill in the technical details? Leave blank if that&rsquo;s you.
        </p>
        <div className="flex flex-col gap-s3">
          <div className="flex gap-s3">
            <div className="min-w-0 flex-1">
              <Input
                label="End user name (optional)"
                value={endUserName}
                onChange={(e) => setEndUserName(e.target.value)}
                placeholder="e.g. Priya"
                disabled={creating}
              />
            </div>
            <div className="min-w-0 flex-1">
              <Input
                label="Team (optional)"
                value={endUserTeam}
                onChange={(e) => setEndUserTeam(e.target.value)}
                placeholder="e.g. Engineering"
                disabled={creating}
              />
            </div>
          </div>
          <Input
            label="End user email (optional)"
            type="email"
            value={endUserEmail}
            onChange={(e) => {
              setEndUserEmail(e.target.value);
              if (emailError) setEmailError(undefined);
            }}
            placeholder="e.g. priya@yourcompany.com"
            disabled={creating}
            error={emailError}
          />
        </div>
        <div className="mt-s6 flex justify-end gap-s2">
          <Button variant="secondary" onClick={close} disabled={creating}>
            Cancel
          </Button>
          {/* Same right-arrow glyph as the Continue gesture elsewhere. */}
          <Button type="submit" disabled={creating || !name.trim()} iconRight={<ArrowRight />}>
            {creating ? 'Creating…' : 'Create RFQ'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default NewRFQModal;
